import { apiClient } from '../client';
import { products } from './resource';
import type {
  AvailabilityRuleAttributes,
  BulkActivateResponse,
  BulkDeactivateResponse,
  BulkPublishResponse,
  CreateProductPayload,
  CreateProductResponse,
  ProductShowResponse,
} from './types';

export interface UpdateProductPayload {
  name?: string;
  category?: string;
  form?: string;
  description?: string | null;
  chinese_name?: string | null;
  byline?: string | null;
  ingredients?: string | null;
  serving_temperature?: string | null;
  kitchen?: string | null;
  kitchen_section?: string[];
  papercut_category?: string | null;
  price_excluding_gst?: number | null;
  cost_to_company?: number | null;
  portion_amount?: number | null;
  portion_unit_label?: string | null;
  notes?: string | null;
  production_notes?: string | null;
  fulfilment_notes?: string | null;
  catalogue_ids?: number[];
  macros?: {
    calories?: number | null;
    carbohydrates?: number | null;
    fats?: number | null;
    protein?: number | null;
  };
  diet_tag_list?: string[];
  allergen_tag_list?: string[];
  nutrition_tag_list?: string[];
  chef_recommended?: boolean;
  bcrs?: boolean;
  display_unit?: string;
  product_inventories_attributes?: Array<{
    id?: number;
    inventory_id: number;
    quantity: number | null;
    _destroy?: boolean;
  }>;
  product_containers_attributes?: Array<{
    id?: number;
    inventory_id: number;
    capacity: number | null;
    minimum_pax_required?: number | null;
    _destroy?: boolean;
  }>;
  availability_rules_attributes?: AvailabilityRuleAttributes[];
  images?: File[];
  remove_image_ids?: string[];
  display_attachment?: File;
  remove_display_attachment?: boolean;
}

function buildFormData(payload: UpdateProductPayload): FormData {
  const formData = new FormData();

  for (const [key, value] of Object.entries(payload)) {
    if (key === 'images') {
      (value as File[]).forEach((file) => {
        formData.append('product[images][]', file);
      });
    } else if (key === 'remove_image_ids') {
      (value as string[]).forEach((id) => {
        formData.append('product[remove_image_ids][]', id);
      });
    } else if (key === 'display_attachment') {
      formData.append('product[display_attachment]', value as File);
    } else if (key === 'catalogue_ids') {
      (value as number[]).forEach((id) => {
        formData.append('product[catalogue_ids][]', String(id));
      });
    } else if (key === 'kitchen_section' && Array.isArray(value)) {
      if (value.length === 0) {
        formData.append('product[kitchen_section][]', '');
      }
      (value as string[]).forEach((token) => {
        formData.append('product[kitchen_section][]', token);
      });
    } else if (key === 'macros' && value != null) {
      for (const [macroKey, macroValue] of Object.entries(value as Record<string, unknown>)) {
        formData.append(`product[macros][${macroKey}]`, macroValue == null ? '' : String(macroValue));
      }
    } else if (key.endsWith('_tag_list') && Array.isArray(value)) {
      (value as string[]).forEach((tag) => {
        formData.append(`product[${key}][]`, tag);
      });
    } else if (key.endsWith('_attributes') && Array.isArray(value)) {
      (value as Record<string, unknown>[]).forEach((item, index) => {
        for (const [attrKey, attrValue] of Object.entries(item)) {
          if (attrValue !== undefined) {
            formData.append(`product[${key}][${index}][${attrKey}]`, attrValue == null ? '' : String(attrValue));
          }
        }
      });
    } else if (value === null) {
      // Explicit null = "clear this field" — send empty string so Rails
      // applies the clear instead of the multipart path silently dropping
      // the key (which would leave the previous value untouched).
      formData.append(`product[${key}]`, '');
    } else if (value !== undefined) {
      formData.append(`product[${key}]`, String(value));
    }
  }

  return formData;
}

function hasFiles(payload: UpdateProductPayload): boolean {
  return (
    (Array.isArray(payload.images) && payload.images.length > 0) ||
    (Array.isArray(payload.remove_image_ids) && payload.remove_image_ids.length > 0) ||
    payload.display_attachment instanceof File ||
    payload.remove_display_attachment === true
  );
}

const MULTIPART_CONFIG = { headers: { 'Content-Type': 'multipart/form-data' } };

export async function updateProduct(id: number, payload: UpdateProductPayload): Promise<ProductShowResponse> {
  if (hasFiles(payload)) {
    const formData = buildFormData(payload);
    const response = await apiClient.patch<ProductShowResponse>(`/api/products/${id}`, formData, MULTIPART_CONFIG);
    return response.data;
  }
  const response = await apiClient.patch<ProductShowResponse>(`/api/products/${id}`, { product: payload });
  return response.data;
}

export async function createProduct(data: CreateProductPayload): Promise<CreateProductResponse> {
  const response = await products.create(data);
  return response.data;
}

export async function duplicateProduct(id: number): Promise<ProductShowResponse> {
  const response = await products.duplicate(id);
  return response.data;
}

export async function activateProduct(id: number): Promise<ProductShowResponse> {
  const response = await products.activate(id);
  return response.data;
}

export async function deactivateProduct(id: number): Promise<ProductShowResponse> {
  const response = await products.deactivate(id);
  return response.data;
}

export async function publishProduct(id: number): Promise<ProductShowResponse> {
  const response = await products.publish(id);
  return response.data;
}

export async function bulkPublishProducts(ids: number[]): Promise<BulkPublishResponse> {
  const response = await products.bulkPublish(ids);
  return response.data;
}

export async function bulkActivateProducts(ids: number[]): Promise<BulkActivateResponse> {
  const response = await products.bulkActivate(ids);
  return response.data;
}

export async function bulkDeactivateProducts(ids: number[]): Promise<BulkDeactivateResponse> {
  const response = await products.bulkDeactivate(ids);
  return response.data;
}
